import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/components.css";
import "./styles/activity.css";
import {
  filterActivities,
  getActivityInitial,
  groupActivitiesByMonth,
} from "./activities.js";
import {
  escapeHtml,
  formatAbsoluteDate,
  formatAction,
  formatAmr,
  formatFlow,
  formatRelativeTime,
  formatShortDate,
  formatStatus,
  getTagValues,
} from "./formatting.js";
import {
  type SupportedLanguage,
  type Translations,
  t,
  translate,
} from "./i18n.js";
import { startSessionKeepalive } from "./session-keepalive.js";
import en from "./translations/activity.en.json";
import nb from "./translations/activity.nb.json";
import nn from "./translations/activity.nn.json";
import type { Activity, PaginatedActivityResponse } from "./types.js";

const dictionaries: Record<SupportedLanguage, Translations> = { en, nb, nn };

// ===========================================
// Configuration
// ===========================================

const API_BASE = "/api";
const SEARCH_DEBOUNCE_MS = 200;

// ===========================================
// Auth Helpers
// ===========================================

class AuthRequiredError extends Error {
  constructor() {
    super("Authentication required");
    this.name = "AuthRequiredError";
  }
}

async function checkSession(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/session/heartbeat`);
    if (response.status === 401) return false;
    // Treat 2xx as authenticated; treat 5xx/network issues as
    // "unknown" — proceed to list view and let fetchActivities()
    // surface errors with its own error-state UI.
    return true;
  } catch {
    // Network failure — assume session may exist, let list view
    // handle the error via fetchActivities().
    return true;
  }
}

// ===========================================
// DOM Helpers
// ===========================================

function getRequiredElement<T extends HTMLElement = HTMLElement>(
  id: string,
): T {
  const el = document.getElementById(id);
  if (!el) {
    throw new Error(`Required element #${id} not found in DOM`);
  }
  return el as T;
}

// ===========================================
// DOM Elements
// ===========================================

// Views
const listViewEl = getRequiredElement("list-view");
const detailViewEl = getRequiredElement("detail-view");

// List view elements
const loadingEl = getRequiredElement("loading");
const errorEl = getRequiredElement("error");
const contentEl = getRequiredElement("content");
const delayNoticeEl = getRequiredElement("delay-notice");
const activityListEl = getRequiredElement("activity-list");
const emptyStateEl = getRequiredElement("empty-state");
const searchEmptyEl = getRequiredElement("search-empty");
const scrollSentinelEl = getRequiredElement("scroll-sentinel");
const retryBtn = getRequiredElement("retry");
const debugInfoEl = getRequiredElement("debug-info");
const demoBannerEl = getRequiredElement("demo-banner");
const searchContainerEl = getRequiredElement("search-container");
const searchInputEl = getRequiredElement<HTMLInputElement>("search-input");
const searchClearEl = getRequiredElement<HTMLButtonElement>("search-clear");

// Detail view elements
const detailBackBtn = getRequiredElement("detail-back");
const detailContentEl = getRequiredElement("detail-content");

// Landing view elements
const landingViewEl = getRequiredElement("landing-view");
const toastContainerEl = getRequiredElement("toast-container");
const logoutFormEl = document.querySelector(".logout-form") as HTMLElement;
const initLoadingEl = getRequiredElement("init-loading");

// ===========================================
// State
// ===========================================

let allActivities: Activity[] = [];
let archiveLoaded = false;
let archiveCursor: string | null = null;
let searchQuery = "";
let listScrollPosition = 0;
let isLoadingMore = false;
let scrollObserver: IntersectionObserver | null = null;

// ===========================================
// View Switching
// ===========================================

type View = "list" | "detail" | "landing";

type HistoryState = { view: "list" } | { view: "detail"; index: number };

function showView(view: View): void {
  initLoadingEl.hidden = true;
  landingViewEl.hidden = view !== "landing";
  listViewEl.hidden = view !== "list";
  detailViewEl.hidden = view !== "detail";

  // Hide logout form on landing view (user is not logged in)
  if (logoutFormEl) {
    logoutFormEl.hidden = view === "landing";
  }

  if (view === "list") {
    window.scrollTo(0, listScrollPosition);
  }
}

// ===========================================
// UI State Management
// ===========================================

type UIState = "loading" | "error" | "loaded" | "empty";

function setUIState(state: UIState): void {
  loadingEl.hidden = state !== "loading";
  errorEl.hidden = state !== "error";
  contentEl.hidden = state === "loading" || state === "error";
  emptyStateEl.hidden = state !== "empty";
  searchContainerEl.hidden = state !== "loaded" && state !== "empty";
  contentEl.setAttribute("aria-busy", state === "loading" ? "true" : "false");
}

// ===========================================
// Rendering - List View
// ===========================================

function renderActivityRow(event: Activity, index: number): string {
  const merchant = escapeHtml(event.actor?.name ?? t("unknown_merchant"));
  const action = formatAction(event.action);
  const relativeTime = formatRelativeTime(event.time);
  const initial = escapeHtml(getActivityInitial(event));
  const statusLabel = formatStatus(event.status);
  const statusHtml = statusLabel
    ? ` <span class="event-row__status event-row__status--${escapeHtml(event.status)}">${escapeHtml(statusLabel)}</span>`
    : "";

  return `
    <div class="event-row" role="listitem" tabindex="0" data-event-index="${index}">
      <div class="event-row__icon" aria-hidden="true">${initial}</div>
      <div class="event-row__content">
        <div class="event-row__merchant">${merchant}</div>
        <div class="event-row__meta">${escapeHtml(action)} \u00b7 ${escapeHtml(relativeTime)}${statusHtml}</div>
      </div>
      <div class="event-row__chevron" aria-hidden="true">&#8250;</div>
    </div>
  `;
}

function renderGroupedActivities(activities: Activity[]): void {
  const filtered = filterActivities(activities, searchQuery);

  if (filtered.length === 0 && searchQuery) {
    activityListEl.innerHTML = "";
    searchEmptyEl.hidden = false;
    scrollSentinelEl.hidden = true;
    disconnectObserver();
    return;
  }

  searchEmptyEl.hidden = true;
  const indexByRef = new Map<Activity, number>();
  for (let i = 0; i < activities.length; i++) {
    indexByRef.set(activities[i], i);
  }
  const groups = groupActivitiesByMonth(filtered);
  const html = groups
    .map((group) => {
      const rows = group.activities
        .map((activity) => {
          const globalIndex = indexByRef.get(activity);
          if (globalIndex === undefined) return "";
          return renderActivityRow(activity, globalIndex);
        })
        .join("");

      return `
        <section class="month-group">
          <h2 class="month-group__header">${escapeHtml(group.label)}</h2>
          <div class="grouped-card">${rows}</div>
        </section>
      `;
    })
    .join("");

  activityListEl.innerHTML = html;
  updateSentinelVisibility();
}

// ===========================================
// Rendering - Detail View
// ===========================================

function renderDetail(event: Activity): void {
  const merchant = escapeHtml(event.actor?.name ?? t("unknown_merchant"));
  const action = formatAction(event.action);
  const flow = formatFlow(getTagValues(event.tags, "flow")[0] ?? "");
  const absoluteDate = formatAbsoluteDate(event.time);
  const shortDate = formatShortDate(event.time);

  const badges = getTagValues(event.tags, "amr")
    .map((amr) => `<span class="badge">${escapeHtml(formatAmr(amr))}</span>`)
    .join("");

  const rows: string[] = [];

  rows.push(`
    <div class="detail-row">
      <span class="detail-row__label">${escapeHtml(t("detail_what"))}</span>
      <span class="detail-row__value">${escapeHtml(action)} \u00b7 ${escapeHtml(flow)}</span>
    </div>
  `);

  rows.push(`
    <div class="detail-row">
      <span class="detail-row__label">${escapeHtml(t("detail_when"))}</span>
      <span class="detail-row__value">${escapeHtml(absoluteDate)}</span>
    </div>
  `);

  const statusLabel = formatStatus(event.status);
  if (statusLabel) {
    rows.push(`
      <div class="detail-row">
        <span class="detail-row__label">${escapeHtml(t("detail_status"))}</span>
        <span class="detail-row__value">${escapeHtml(statusLabel)}</span>
      </div>
    `);
  }

  detailContentEl.innerHTML = `
    <div class="detail-header">
      <h2 class="detail-header__title">${merchant}</h2>
      <p class="detail-header__subtitle">${escapeHtml(action)}, ${escapeHtml(shortDate)}</p>
    </div>

    <section class="detail-section">
      <h3 class="detail-section__header">${escapeHtml(t("detail_details"))}</h3>
      <div class="detail-card">
        ${rows.join("")}
      </div>
    </section>

    ${
      badges
        ? `
      <section class="detail-section">
        <h3 class="detail-section__header">${escapeHtml(t("detail_method"))}</h3>
        <div class="detail-card">
          <div class="detail-badges">${badges}</div>
        </div>
      </section>
    `
        : ""
    }

    <button class="detail-report-btn" type="button">${escapeHtml(t("detail_report_error"))}</button>
  `;
}

// ===========================================
// API Calls
// ===========================================

async function fetchActivities(): Promise<PaginatedActivityResponse> {
  const response = await fetch(`${API_BASE}/me/activities?limit=200`);
  if (response.status === 401) {
    showLandingView();
    throw new AuthRequiredError(); // Abort further processing
  }
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

async function fetchMoreActivities(): Promise<PaginatedActivityResponse> {
  const params = new URLSearchParams();
  params.set("limit", "10");
  if (archiveCursor) {
    params.set("before", archiveCursor);
  }
  const url = `${API_BASE}/me/activities?${params}`;
  const response = await fetch(url);
  if (response.status === 401) {
    showLandingView();
    throw new AuthRequiredError();
  }
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

// ===========================================
// Infinite Scroll
// ===========================================

function updateSentinelVisibility(): void {
  const shouldShow = searchQuery === "" && !archiveLoaded;
  scrollSentinelEl.hidden = !shouldShow;

  if (shouldShow) {
    observeSentinel();
  } else {
    disconnectObserver();
  }
}

function observeSentinel(): void {
  if (scrollObserver) return; // already observing

  scrollObserver = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) {
        loadMoreActivities();
      }
    },
    { rootMargin: "200px" },
  );
  scrollObserver.observe(scrollSentinelEl);
}

function disconnectObserver(): void {
  if (scrollObserver) {
    scrollObserver.disconnect();
    scrollObserver = null;
  }
}

function showSentinelState(state: "loading" | "error"): void {
  const spinner = scrollSentinelEl.querySelector(
    ".scroll-sentinel__spinner",
  ) as HTMLElement;
  const srSpan = scrollSentinelEl.querySelector(
    ".visually-hidden",
  ) as HTMLElement;
  if (state === "loading") {
    scrollSentinelEl.hidden = false;
    spinner.hidden = false;
    if (srSpan) srSpan.hidden = false;
    scrollSentinelEl.querySelector(".scroll-sentinel__error")?.remove();
  } else {
    spinner.hidden = true;
    if (srSpan) srSpan.hidden = true;
    // Remove any existing error before adding new one
    scrollSentinelEl.querySelector(".scroll-sentinel__error")?.remove();
    const errorEl = document.createElement("p");
    errorEl.className = "scroll-sentinel__error";
    errorEl.innerHTML = t("load_error_retry");
    scrollSentinelEl.appendChild(errorEl);
    // Attach retry handler
    const retryLink = errorEl.querySelector("#scroll-retry");
    if (retryLink) {
      retryLink.addEventListener("click", (e) => {
        e.preventDefault();
        observeSentinel();
        loadMoreActivities();
      });
    }
  }
}

// ===========================================
// Event Handlers
// ===========================================

const TOAST_AUTO_DISMISS_MS = 8000;

function handleTransientMessages(): void {
  const params = new URLSearchParams(window.location.search);
  let messageKey: string | null = null;

  if (params.get("logged_out") === "1") {
    messageKey = "logout_confirmation";
  } else if (params.get("session_expired") === "1") {
    messageKey = "session_expired";
  }

  if (messageKey) {
    toastContainerEl.textContent = t(messageKey);
    toastContainerEl.hidden = false;

    // Clean the URL
    history.replaceState(history.state, "", window.location.pathname);

    // Auto-dismiss
    setTimeout(() => {
      toastContainerEl.hidden = true;
    }, TOAST_AUTO_DISMISS_MS);
  }
}

function showLandingView(): void {
  showView("landing");
  loadingEl.hidden = true;
  errorEl.hidden = true;
  handleTransientMessages();
}

async function loadActivities(): Promise<void> {
  setUIState("loading");

  try {
    const data = await fetchActivities();
    allActivities = data.activities;

    // Track pagination cursor from initial load
    if (data.pagination?.hasMore && data.pagination.nextCursor) {
      archiveCursor = data.pagination.nextCursor;
      archiveLoaded = false;
    } else {
      archiveLoaded = true;
    }

    if (data.debugUserIdMasked) {
      debugInfoEl.textContent = `Debug: User ID ${data.debugUserIdMasked} | Activities: ${data.activities.length}`;
      debugInfoEl.hidden = false;
    }

    if (data.isTestEnvironment) {
      demoBannerEl.hidden = false;
    }

    if (data.dataDelayNotice) {
      delayNoticeEl.textContent = data.dataDelayNotice;
      delayNoticeEl.hidden = false;
    }

    if (allActivities.length === 0) {
      setUIState("empty");
    } else {
      renderGroupedActivities(allActivities);
      setUIState("loaded");
    }

    // Start session keepalive heartbeat (confirmed authenticated)
    startSessionKeepalive();
  } catch (error) {
    if (error instanceof AuthRequiredError) return;
    console.error("Failed to load activities:", error);
    setUIState("error");
  }
}

async function loadMoreActivities(): Promise<void> {
  if (archiveLoaded || isLoadingMore) return;

  isLoadingMore = true;
  showSentinelState("loading");

  try {
    const archiveData = await fetchMoreActivities();
    allActivities = [...allActivities, ...archiveData.activities];
    if (archiveData.pagination?.hasMore && archiveData.pagination.nextCursor) {
      archiveCursor = archiveData.pagination.nextCursor;
    } else {
      archiveLoaded = true;
    }
    renderGroupedActivities(allActivities);
  } catch (error) {
    if (error instanceof AuthRequiredError) return;
    console.error("Failed to load more activities:", error);
    showSentinelState("error");
    disconnectObserver(); // stop observing until retry
  } finally {
    isLoadingMore = false;
  }
}

function handleEventClick(index: number): void {
  const event = allActivities[index];
  if (!event) return;

  listScrollPosition = window.scrollY;
  renderDetail(event);
  history.pushState({ view: "detail", index } satisfies HistoryState, "");
  showView("detail");
  window.scrollTo(0, 0);
}

function handleBackToList(): void {
  history.back();
}

let searchTimeout: ReturnType<typeof setTimeout> | null = null;

function handleSearchInput(): void {
  const query = searchInputEl.value.trim();
  searchClearEl.hidden = query.length === 0;

  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    searchQuery = query;
    renderGroupedActivities(allActivities);
  }, SEARCH_DEBOUNCE_MS);
}

function handleSearchClear(): void {
  searchInputEl.value = "";
  searchClearEl.hidden = true;
  searchQuery = "";
  renderGroupedActivities(allActivities);
  searchInputEl.focus();
}

// ===========================================
// Initialize
// ===========================================

async function init(): Promise<void> {
  translate(dictionaries);

  // Check authentication before showing any view
  const isAuthenticated = await checkSession();
  if (!isAuthenticated) {
    showLandingView();
    return;
  }

  // User is authenticated — show the activity list view
  showView("list");

  history.replaceState({ view: "list" } satisfies HistoryState, "");

  if (
    !(window as unknown as Record<string, unknown>).__argusPopstateRegistered
  ) {
    (window as unknown as Record<string, unknown>).__argusPopstateRegistered =
      true;
    window.addEventListener("popstate", (e: PopStateEvent) => {
      const state = e.state as HistoryState | null;
      if (state?.view === "detail") {
        const event = allActivities[state.index];
        if (event) {
          renderDetail(event);
          showView("detail");
          window.scrollTo(0, 0);
        } else {
          showView("list");
        }
      } else {
        showView("list");
      }
    });
  }

  retryBtn.addEventListener("click", loadActivities);
  detailBackBtn.addEventListener("click", handleBackToList);
  searchInputEl.addEventListener("input", handleSearchInput);
  searchClearEl.addEventListener("click", handleSearchClear);

  activityListEl.addEventListener("click", (e: Event) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>(".event-row");
    if (target) {
      const index = Number(target.dataset.eventIndex);
      handleEventClick(index);
    }
  });

  activityListEl.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      const target = (e.target as HTMLElement).closest<HTMLElement>(
        ".event-row",
      );
      if (target) {
        e.preventDefault();
        const index = Number(target.dataset.eventIndex);
        handleEventClick(index);
      }
    }
  });

  loadActivities();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
